/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import { request } from "../../config/helpers/axios-instance";
import Modal from '../Modal';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const TYPE_OPTIONS = ["TABLET", "CAPSULE", "INJECTION", "SACHET", "BOX", "SYRUP", "POWDER", "CREAM", "OINTMENT"];

const SHAPE_OPTIONS = ["CIRCULAR", "RECTANGLE", "CYLINDRICAL"];
const EMPTY_NEW_MEDICINE = { genericName: '', manufacture: '', medicineType: 'TABLET', shape: '', color: '', size: '', strength: '', description: '' };

/**
 * Checks if the element is visible in the scroll view
 */
const isVisible = (container, ele, direction) => {
    const containerRect = container.getBoundingClientRect();
    const { bottom, top } = ele.getBoundingClientRect();

    if (direction === 'top') {
        return top >= containerRect.top;
    } else {
        return bottom <= containerRect.bottom;
    }
}

const CreateSubstituteModal = ({ onClose, onSuccess, quotationId, prescriptionItems, editingSubstitute, existingSubstitutions = [], orderId }) => {
    const [selectedOriginalItem, setSelectedOriginalItem] = useState(null);
    const [selectedSubstituteItem, setSelectedSubstituteItem] = useState(null);
    const [searchedOriginalItems, setSearchedOriginalItems] = useState(null);
    const [isEditInitialized, setIsEditInitialized] = useState(false);
    const [userHasStartedTyping, setUserHasStartedTyping] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isInitialMount, setIsInitialMount] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newMedicine, setNewMedicine] = useState(EMPTY_NEW_MEDICINE);
    const [addingMedicine, setAddingMedicine] = useState(false);

    const originalSearchRef = useRef();
    const [originalItemHovered, setOriginalItemHovered] = useState(null);

    // Convert prescriptionItems object to array and get unique items
    const quotationItemsArray = Object.values(prescriptionItems || {});
    const uniqueQuotationItems = quotationItemsArray.reduce((unique, item) => {
        // Check if an item with the same itemId already exists
        const existingItem = unique.find(uniqueItem => uniqueItem.itemId === item.itemId);
        if (!existingItem) {
            unique.push(item);
        }
        return unique;
    }, []);

    // Filter out items that are already used as substitutes (except when editing the same substitution)
    const availableQuotationItems = uniqueQuotationItems.filter(item => {
        // Get all existing substitutedItemIds except the one being edited
        const usedSubstituteIds = existingSubstitutions
            .filter(sub => editingSubstitute ? sub.id !== editingSubstitute.id : true)
            .map(sub => parseInt(sub.substitutedItemId));

        // Include item if it's not already used as a substitute
        const isAlreadyUsed = usedSubstituteIds.includes(parseInt(item.itemId));

        return !isAlreadyUsed;
    });

    /**
     * Saves the substitute item
     */
    const saveSubstitute = async (type) => {
        if (!selectedOriginalItem || !selectedSubstituteItem) {
            toast.error('Please select both original item and substitute item');
            return;
        }

        try {
            const payload = {
                itemId: parseInt(selectedOriginalItem.id),
                substitutedItemId: parseInt(selectedSubstituteItem.itemId),
                quotationId: parseInt(quotationId),
                type,
            };

            if (orderId) {
                payload.orderId = parseInt(orderId);
            }

            if (editingSubstitute) {
                const response = await request({
                    url: `itemSubstitute/admin/${editingSubstitute.id}`,
                    method: 'PUT',
                    headers: { "Content-Type": "application/json" },
                    data: payload
                });
                if (response?.data?.success) {
                    toast.success('Substitute item updated successfully');
                } else {
                    toast.error('Failed to update substitute item');
                }
            } else {
                const response = await request({
                    url: `itemSubstitute/admin`,
                    method: 'POST',
                    headers: { "Content-Type": "application/json" },
                    data: payload
                });
                if (response?.data?.success) {
                    toast.success('Substitute item created successfully');
                } else {
                    toast.error('Failed to create substitute item');
                }
            }

            onSuccess();
        } catch (error) {
            const errorMessage = error?.response?.data?.message ||
                error?.response?.data?.error ||
                'Unknown error occurred';
            toast.error(`${editingSubstitute ? 'Failed to update' : 'Failed to create'} substitute item: ${errorMessage}`);
        }
    };

    /**
     * Fetches original items by search term
     */
    const fetchOriginalItemsByName = async (name) => {
        if (!name) {
            setSearchedOriginalItems(null);
            return;
        }

        try {
            const response = await request({
                url: `item/admin`,
                method: "GET",
                params: { name }
            });

            if (response?.data?.data?.rows.length !== 0) {
                setOriginalItemHovered({
                    id: response?.data?.data?.rows[0]?.id,
                    index: 0
                });
            }
            setSearchedOriginalItems(response?.data?.data?.rows);
        } catch (error) {
            console.error('Error fetching original items:', error);
            setSearchedOriginalItems([]);
        }
    };

    /**
     * Handles changes for original item search input
     */
    const handleOriginalItemSearch = async (searchTerm) => {
        // Mark that user has started typing to prevent edit re-initialization
        setUserHasStartedTyping(true);

        // Clear current selection when user starts typing something different
        if (searchTerm && searchTerm.trim() !== selectedOriginalItem?.name?.trim()) {
            setSelectedOriginalItem(null);
        }

        setShowAddForm(false);
        setNewMedicine(EMPTY_NEW_MEDICINE);

        // Search if there's a search term (minimum 2 characters)
        if (searchTerm && searchTerm.trim().length >= 2) {
            await fetchOriginalItemsByName(searchTerm.trim());
        } else if (searchTerm && searchTerm.trim().length === 1) {
            // Show message for single character
            setSearchedOriginalItems([]);
        } else {
            // Clear results if empty
            setSearchedOriginalItems(null);
        }
    };

    /**
     * Clear original item selection
     */
    const clearOriginalSelection = () => {
        setUserHasStartedTyping(true); // Prevent re-initialization
        setSelectedOriginalItem(null);
        setSearchedOriginalItems(null);
        setInputValue('');
        if (originalSearchRef.current) {
            originalSearchRef.current.value = '';
            setTimeout(() => {
                if (originalSearchRef.current) {
                    originalSearchRef.current.focus();
                }
            }, 50);
        }
    };

    /**
     * Clear substitute item selection
     */
    const clearSubstituteSelection = () => {
        setSelectedSubstituteItem(null);
    };

    /**
     * Reset entire form
     */
    const resetForm = () => {
        setUserHasStartedTyping(false); // Allow re-initialization
        setIsEditInitialized(false);
        setSelectedOriginalItem(null);
        setSelectedSubstituteItem(null);
        setSearchedOriginalItems(null);
        setInputValue('');
        if (originalSearchRef.current) {
            originalSearchRef.current.value = '';
        }
    };

    /**
     * Handles key press navigation for original items
     */
    const keyPressHandler = (e) => {
        const container = document.getElementById('original-items');

        if (e.key === "ArrowDown") {
            if (searchedOriginalItems && searchedOriginalItems?.length !== 0) {
                setOriginalItemHovered((prevState) => {
                    if (prevState?.index !== searchedOriginalItems?.length - 1) {
                        const ele = document.getElementById(`original-#${searchedOriginalItems[(prevState?.index + 1) % searchedOriginalItems?.length]?.id}`);

                        if (!isVisible(container, ele, "bottom")) {
                            ele.scrollIntoView(false);
                        }

                        return {
                            id: searchedOriginalItems[(prevState?.index + 1) % searchedOriginalItems?.length]?.id,
                            index: (prevState?.index + 1) % searchedOriginalItems?.length
                        }
                    } else {
                        return prevState;
                    }
                })
            }
        }

        if (e.key === "ArrowUp") {
            if (searchedOriginalItems && searchedOriginalItems.length !== 0) {
                setOriginalItemHovered((prevState) => {
                    if (prevState.index !== 0) {
                        const ele = document.getElementById(`original-#${searchedOriginalItems[(prevState.index - 1) % searchedOriginalItems?.length]?.id}`);

                        if (!isVisible(container, ele, "top")) {
                            ele.scrollIntoView(true);
                        }

                        return {
                            id: searchedOriginalItems[(prevState.index - 1) % searchedOriginalItems?.length]?.id,
                            index: (prevState.index - 1) % searchedOriginalItems?.length
                        }
                    } else {
                        return prevState;
                    }
                })
            }
        }

        if (e.key === "Enter") {
            const currOriginal = searchedOriginalItems && searchedOriginalItems?.filter((item) => item.id === originalItemHovered.id)[0];
            if (currOriginal) {
                setSelectedOriginalItem(currOriginal);
                setInputValue(currOriginal.name);
                if (originalSearchRef.current) {
                    originalSearchRef.current.value = currOriginal.name;
                }
                setSearchedOriginalItems(null); // Hide dropdown after selection
            }
        }
    };

    /**
     * Handle Escape key to close modal
     */
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        }
    }, [onClose]);

    // Initialize with editing data if provided
    useEffect(() => {
        if (editingSubstitute && !isEditInitialized && !userHasStartedTyping) {

            // Set the original item (now from search) - this should be the external item
            const originalItem = editingSubstitute.originalItem;
            setSelectedOriginalItem(originalItem);

            // Set the input value (only if user hasn't started typing)
            if (!userHasStartedTyping) {
                setInputValue(originalItem?.name || '');
                setTimeout(() => {
                    if (originalSearchRef.current && originalItem?.name && !userHasStartedTyping) {
                        originalSearchRef.current.value = originalItem.name;
                    }
                }, 100);
            }

            // Find the substitute item from available quotation items - this should be from the quotation
            const substituteItem = availableQuotationItems.find(item => {
                return item.itemId == editingSubstitute.substitutedItemId; // Use == for loose comparison
            });

            if (substituteItem) {
                setSelectedSubstituteItem(substituteItem);
            } else {
                console.warn('Could not find substitute item in available quotation items');
                // Try finding in all unique items (including used ones) for editing
                const directMatch = uniqueQuotationItems.find(item =>
                    item.itemId == editingSubstitute.substitutedItemId ||
                    item.item?.id == editingSubstitute.substitutedItem?.id ||
                    item.item?.name === editingSubstitute.substitutedItem?.name
                );
                if (directMatch) {
                    setSelectedSubstituteItem(directMatch);
                }
            }

            setIsEditInitialized(true);
        } else if (!editingSubstitute && isInitialMount) {
            // Only clear form on initial mount for create mode
            setIsEditInitialized(false);
            setUserHasStartedTyping(false);
            setSelectedOriginalItem(null);
            setSelectedSubstituteItem(null);
            setSearchedOriginalItems(null);
            setInputValue('');
            if (originalSearchRef.current) {
                originalSearchRef.current.value = '';
            }
            setIsInitialMount(false);
        }
    }, [editingSubstitute, availableQuotationItems, uniqueQuotationItems, isEditInitialized, userHasStartedTyping, isInitialMount]);

    const handleAddNewMedicine = async () => {
        if (!inputValue.trim()) {
            toast.error('Medicine name is required');
            return;
        }
        setAddingMedicine(true);
        try {
            const response = await request({
                url: 'item/admin',
                method: 'POST',
                data: {
                    name: inputValue.trim(),
                    ...(newMedicine.genericName && { genericName: newMedicine.genericName.trim() }),
                    ...(newMedicine.manufacture && { manufacture: newMedicine.manufacture.trim() }),
                    medicineType: newMedicine.medicineType,
                    ...(newMedicine.shape && { shape: newMedicine.shape }),
                    ...(newMedicine.color && { color: newMedicine.color.trim() }),
                    ...(newMedicine.size && { size: parseInt(newMedicine.size) }),
                    ...(newMedicine.strength && { strength: newMedicine.strength.trim() }),
                    ...(newMedicine.description && { description: newMedicine.description.trim() }),
                    source: 'MANUAL_SUBSTITUTION',
                    tenantId: parseInt(Cookies.get('tenantId') || '1'),
                },
            });
            if (response?.data?.success) {
                const created = response.data.data;
                toast.success('Medicine added successfully');
                setSelectedOriginalItem(created);
                setShowAddForm(false);
                setNewMedicine(EMPTY_NEW_MEDICINE);
                setSearchedOriginalItems(null);
            } else {
                toast.error(response?.data?.err || 'Failed to add medicine');
            }
        } catch {
            toast.error('Failed to add medicine');
        } finally {
            setAddingMedicine(false);
        }
    };

    return (
        <Modal onClose={onClose} title={editingSubstitute ? "Edit Substitute Item" : "Create Substitute Item"} contentClassName="md:max-w-7xl">
            <div className='flex flex-row gap-4 h-auto max-h-[580px]'>
                {/* Left side - Search for Original Items */}
                <div className='w-[50%] flex flex-col gap-1'>
                    <div className="flex justify-between items-center">
                        <strong>{`Original Item to Replace: ${selectedOriginalItem?.name || ''}`}</strong>
                        <div className="flex items-center gap-2">
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                Input: {inputValue}
                            </span>
                            {editingSubstitute && (
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                    Editing Mode
                                </span>
                            )}
                        </div>
                    </div>
                    <input
                        autoComplete='off'
                        name='original-input'
                        onKeyDown={keyPressHandler}
                        autoFocus={!editingSubstitute}
                        ref={originalSearchRef}
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            handleOriginalItemSearch(e.target.value);
                        }}
                        className='w-full rounded-md p-2 border border-gray-300'
                        type='text'
                        placeholder={editingSubstitute ? "Type to change original item" : "Search for item to replace"}
                    />
                    <div id="original-items" className='flex flex-col gap-1 overflow-y-scroll h-full max-h-[420px] border border-gray-200 rounded-md p-2'>
                        {searchedOriginalItems === null ? (
                            <p className="text-gray-500 text-center py-4 text-sm">Type to search for items...</p>
                        ) : searchedOriginalItems.length === 0 ? (
                            originalSearchRef.current?.value?.length === 1 ? (
                                <p className="text-gray-500 text-center py-4 text-sm">Type at least 2 characters...</p>
                            ) : showAddForm ? (
                                <div className="flex flex-col gap-3 p-2">
                                    <p className="text-sm font-semibold text-gray-700">Adding <span className="text-[#28A48B]">&quot;{inputValue}&quot;</span></p>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-gray-500 font-medium">Generic Name</label>
                                        <input
                                            type="text"
                                            value={newMedicine.genericName}
                                            onChange={(e) => setNewMedicine(p => ({ ...p, genericName: e.target.value }))}
                                            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                            placeholder="e.g. Paracetamol"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-gray-500 font-medium">Manufacturer</label>
                                        <input
                                            type="text"
                                            value={newMedicine.manufacture}
                                            onChange={(e) => setNewMedicine(p => ({ ...p, manufacture: e.target.value }))}
                                            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                            placeholder="e.g. GSK"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-gray-500 font-medium">Type</label>
                                        <select
                                            value={newMedicine.medicineType}
                                            onChange={(e) => setNewMedicine(p => ({ ...p, medicineType: e.target.value }))}
                                            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                        >
                                            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex flex-col gap-1 flex-1">
                                            <label className="text-xs text-gray-500 font-medium">Shape</label>
                                            <select
                                                value={newMedicine.shape}
                                                onChange={(e) => setNewMedicine(p => ({ ...p, shape: e.target.value }))}
                                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                            >
                                                <option value="">Select</option>
                                                {SHAPE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1 flex-1">
                                            <label className="text-xs text-gray-500 font-medium">Color</label>
                                            <input
                                                type="text"
                                                value={newMedicine.color}
                                                onChange={(e) => setNewMedicine(p => ({ ...p, color: e.target.value }))}
                                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                                placeholder="e.g. White"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex flex-col gap-1 flex-1">
                                            <label className="text-xs text-gray-500 font-medium">Size</label>
                                            <input
                                                type="number"
                                                value={newMedicine.size}
                                                onChange={(e) => setNewMedicine(p => ({ ...p, size: e.target.value }))}
                                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                                placeholder="e.g. 10"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1 flex-1">
                                            <label className="text-xs text-gray-500 font-medium">Strength</label>
                                            <input
                                                type="text"
                                                value={newMedicine.strength}
                                                onChange={(e) => setNewMedicine(p => ({ ...p, strength: e.target.value }))}
                                                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B]"
                                                placeholder="e.g. 500mg"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-gray-500 font-medium">Description</label>
                                        <textarea
                                            value={newMedicine.description}
                                            onChange={(e) => setNewMedicine(p => ({ ...p, description: e.target.value }))}
                                            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-[#28A48B] resize-none"
                                            rows={2}
                                            placeholder="Optional description..."
                                        />
                                    </div>
                                    <div className="flex gap-2 mt-1">
                                        <button
                                            onClick={() => { setShowAddForm(false); setNewMedicine(EMPTY_NEW_MEDICINE); }}
                                            className="flex-1 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleAddNewMedicine}
                                            disabled={addingMedicine}
                                            className="flex-1 py-1.5 text-sm rounded-md bg-[#28A48B] text-white-50 font-semibold hover:opacity-90 disabled:opacity-50"
                                        >
                                            {addingMedicine ? 'Adding...' : 'Add & Select'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-3 py-4">
                                    <p className="text-gray-500 text-sm">No results for &quot;{inputValue}&quot;</p>
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        className="flex items-center gap-1 text-sm text-[#28A48B] font-semibold hover:underline"
                                    >
                                        <span>+</span> Add &quot;{inputValue}&quot; as new medicine
                                    </button>
                                </div>
                            )
                        ) : (
                            searchedOriginalItems.map((item, idx) => {
                                const isSelected = selectedOriginalItem?.id === item.id;
                                const isHovered = originalItemHovered?.id === item.id && originalItemHovered?.index === idx;

                                return (
                                    <p
                                        key={item.id}
                                        id={`original-#${item.id}`}
                                        className={`hover:cursor-pointer p-2 rounded transition-colors ${isSelected
                                            ? 'bg-blue-200 border border-blue-400'
                                            : isHovered
                                                ? 'bg-gray-200'
                                                : 'hover:bg-gray-100'
                                            }`}
                                        onClick={() => {
                                            setSelectedOriginalItem(item);
                                            setInputValue(item.name);
                                            if (originalSearchRef.current) {
                                                originalSearchRef.current.value = item.name;
                                            }
                                            setSearchedOriginalItems(null); // Hide dropdown after selection
                                        }}
                                    >
                                        {item.name} <br />
                                        <span className='text-sm text-purple-600 font-bold'>{item.mnemonic}</span>
                                    </p>
                                );
                            })
                        )}
                    </div>
                    {selectedOriginalItem && (
                        <div className="mt-2 p-2 bg-blue-50 rounded-md">
                            <div className="flex justify-between items-center">
                                <div>
                                    <strong className="text-sm">Selected:</strong>
                                    <div className="text-sm">{selectedOriginalItem.name}</div>
                                </div>
                                <button
                                    onClick={clearOriginalSelection}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                    title="Clear selection"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className='border-solid border-[1px] border-[#ccc]'></div>

                {/* Right side - Quotation Items as Substitutes */}
                <div className='w-[50%] flex flex-col gap-1'>
                    <div className="flex justify-between items-center">
                        <strong>Select Substitute Item from Quotation:</strong>
                        {editingSubstitute && (
                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                Click to Change
                            </span>
                        )}
                    </div>
                    <div className='flex flex-col gap-1 overflow-y-scroll h-full max-h-[420px] border border-gray-200 rounded-md p-2'>
                        {availableQuotationItems.length > 0 ? (
                            availableQuotationItems.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => setSelectedSubstituteItem(item)}
                                    className={`p-3 rounded-md cursor-pointer border transition-colors ${selectedSubstituteItem?.id === item.id
                                        ? 'bg-green-100 border-green-300'
                                        : 'hover:bg-gray-100 border-gray-200'
                                        }`}
                                >
                                    <div className="font-semibold text-sm">{item.item?.name}</div>
                                    <div className="text-xs text-gray-500">ID: {item.itemId}</div>
                                    <div className="text-xs text-gray-500">
                                        Batch: {item.itemBatch?.batch || 'No batch'}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 text-center py-4">
                                {uniqueQuotationItems.length === 0
                                    ? "No items found in quotation"
                                    : "All quotation items are already used as substitutes"
                                }
                            </p>
                        )}
                    </div>
                    {selectedSubstituteItem && (
                        <div className="mt-2 p-2 bg-green-50 rounded-md">
                            <div className="flex justify-between items-center">
                                <div>
                                    <strong className="text-sm">Selected:</strong>
                                    <div className="text-sm">{selectedSubstituteItem.item?.name}</div>
                                </div>
                                <button
                                    onClick={clearSubstituteSelection}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                    title="Clear selection"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Debug Information */}
            {editingSubstitute && (
                <div className="mt-4 p-3 bg-gray-100 rounded-md text-xs">
                    <strong>Debug Info:</strong>
                    <div>Edit Data: {JSON.stringify({
                        originalItem: editingSubstitute.originalItem?.name,
                        substitutedItemId: editingSubstitute.substitutedItemId,
                        substitutedItem: editingSubstitute.substitutedItem?.name
                    })}</div>
                    <div>Selected Original: {selectedOriginalItem?.name || 'None'}</div>
                    <div>Selected Substitute: {selectedSubstituteItem?.item?.name || 'None'}</div>
                    <div>Total Unique Items: {uniqueQuotationItems.length}</div>
                    <div>Available Items (filtered): {availableQuotationItems.length}</div>
                    <div>Existing Substitutions: {existingSubstitutions.length}</div>
                    <div>Is Edit Initialized: {isEditInitialized.toString()}</div>
                    <div>User Has Started Typing: {userHasStartedTyping.toString()}</div>
                    <div>Input Value: {originalSearchRef.current?.value || 'None'}</div>
                </div>
            )}

            <hr className="my-4" />

            <div className='flex flex-row gap-[10px]'>
                <button
                    onClick={() => saveSubstitute('SIMILAR')}
                    className='px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-md text-white-50 font-medium disabled:opacity-50'
                    disabled={!selectedOriginalItem || !selectedSubstituteItem}
                >
                    {editingSubstitute ? 'Update as Similar' : 'Add as Similar'}
                </button>
                <button
                    onClick={() => saveSubstitute('SUBSTITUTION')}
                    className='px-4 py-2 bg-[#049079] hover:bg-[#049079]/[0.85] rounded-md text-white-50 font-medium disabled:opacity-50'
                    disabled={!selectedOriginalItem || !selectedSubstituteItem}
                >
                    {editingSubstitute ? 'Update as Substitution' : 'Add as Substitution'}
                </button>
                {editingSubstitute && (
                    <button
                        onClick={resetForm}
                        className='px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-md text-white-50 font-medium'
                    >
                        Reset Form
                    </button>
                )}
                <button
                    onClick={onClose}
                    className='px-4 py-2 bg-gray-500 hover:bg-gray-600 rounded-md text-white-50 font-medium'
                >
                    Cancel
                </button>
            </div>
        </Modal>
    );
};

export default CreateSubstituteModal;
